import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  BellIcon,
  CheckIcon,
  TrashIcon,
  FunnelIcon,
  Cog6ToothIcon,
  CloudIcon,
  HeartIcon,
  ShieldExclamationIcon,
  BeakerIcon,
  CurrencyDollarIcon,
  WrenchIcon,
  UserGroupIcon,
  ComputerDesktopIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Card, CardHeader, CardTitle, Button } from '../components/ui';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

// ============ CATEGORY STYLING ============

const CATEGORY_CONFIG: Record<
  string,
  { label: string; color: string; borderColor: string; bgColor: string; icon: typeof BellIcon }
> = {
  weather: {
    label: 'Weather',
    color: 'text-blue-600',
    borderColor: 'border-l-blue-500',
    bgColor: 'bg-blue-50',
    icon: CloudIcon,
  },
  crop_health: {
    label: 'Crop Health',
    color: 'text-green-600',
    borderColor: 'border-l-green-500',
    bgColor: 'bg-green-50',
    icon: HeartIcon,
  },
  disease: {
    label: 'Disease',
    color: 'text-red-600',
    borderColor: 'border-l-red-500',
    bgColor: 'bg-red-50',
    icon: ShieldExclamationIcon,
  },
  irrigation: {
    label: 'Irrigation',
    color: 'text-cyan-600',
    borderColor: 'border-l-cyan-500',
    bgColor: 'bg-cyan-50',
    icon: BeakerIcon,
  },
  market: {
    label: 'Market',
    color: 'text-amber-600',
    borderColor: 'border-l-amber-500',
    bgColor: 'bg-amber-50',
    icon: CurrencyDollarIcon,
  },
  equipment: {
    label: 'Equipment',
    color: 'text-purple-600',
    borderColor: 'border-l-purple-500',
    bgColor: 'bg-purple-50',
    icon: WrenchIcon,
  },
  cooperative: {
    label: 'Cooperative',
    color: 'text-indigo-600',
    borderColor: 'border-l-indigo-500',
    bgColor: 'bg-indigo-50',
    icon: UserGroupIcon,
  },
  system: {
    label: 'System',
    color: 'text-gray-600',
    borderColor: 'border-l-gray-400',
    bgColor: 'bg-gray-50',
    icon: ComputerDesktopIcon,
  },
};

const TYPE_ICON: Record<string, typeof BellIcon> = {
  alert: ExclamationCircleIcon,
  warning: ExclamationTriangleIcon,
  success: CheckCircleIcon,
  info: InformationCircleIcon,
  system: ComputerDesktopIcon,
};

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

// ============ NOTIFICATION CARD ============

function NotificationCard({
  notification,
  onMarkRead,
  onDelete,
  onNavigate,
}: {
  notification: any;
  onMarkRead: (id: string) => void;
  onDelete: (id: string) => void;
  onNavigate: (link: string) => void;
}) {
  const catConfig = CATEGORY_CONFIG[notification.category] || CATEGORY_CONFIG.system;
  const TypeIcon = TYPE_ICON[notification.type] || InformationCircleIcon;
  const CatIcon = catConfig.icon;

  return (
    <div
      className={`
        relative flex items-start gap-3 p-4 rounded-xl border-l-4 transition-all duration-200
        ${catConfig.borderColor}
        ${notification.is_read ? 'bg-white opacity-75' : 'bg-white shadow-sm'}
        ${notification.link ? 'cursor-pointer hover:shadow-md' : ''}
      `}
      onClick={() => {
        if (notification.link) {
          if (!notification.is_read) onMarkRead(notification.id);
          onNavigate(notification.link);
        }
      }}
    >
      {/* Icon */}
      <div className={`flex-shrink-0 p-2 rounded-lg ${catConfig.bgColor}`}>
        <CatIcon className={`h-5 w-5 ${catConfig.color}`} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <h4 className={`text-sm font-semibold ${notification.is_read ? 'text-gray-600' : 'text-gray-900'}`}>
              {notification.title}
            </h4>
            {!notification.is_read && (
              <span className="w-2 h-2 rounded-full bg-kenagri-500 flex-shrink-0" />
            )}
          </div>
          <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
            {timeAgo(notification.created_at)}
          </span>
        </div>
        <p className={`text-sm mt-1 ${notification.is_read ? 'text-gray-400' : 'text-gray-600'}`}>
          {notification.message}
        </p>
        <div className="flex items-center gap-2 mt-2">
          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${catConfig.bgColor} ${catConfig.color}`}>
            <TypeIcon className="h-3 w-3" />
            {catConfig.label}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        {!notification.is_read && (
          <button
            onClick={() => onMarkRead(notification.id)}
            className="p-1.5 rounded-lg text-gray-400 hover:text-kenagri-600 hover:bg-kenagri-50 transition-colors"
            title="Mark as read"
          >
            <CheckIcon className="h-4 w-4" />
          </button>
        )}
        <button
          onClick={() => onDelete(notification.id)}
          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
          title="Delete"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ============ PREFERENCES TAB ============

function PreferencesTab() {
  const queryClient = useQueryClient();

  const { data: prefs, isLoading } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/notifications/preferences`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to load preferences');
      return res.json();
    },
  });

  const [localPrefs, setLocalPrefs] = useState<any>(null);

  useEffect(() => {
    if (prefs && !localPrefs) {
      setLocalPrefs(prefs);
    }
  }, [prefs, localPrefs]);

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch(`${API_URL}/notifications/preferences`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to update preferences');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      toast.success('Preferences saved');
    },
    onError: () => toast.error('Failed to save preferences'),
  });

  const handleSave = () => {
    if (!localPrefs) return;
    updateMutation.mutate({
      channels: localPrefs.channels,
      categories: localPrefs.categories,
      quiet_hours_start: localPrefs.quiet_hours_start,
      quiet_hours_end: localPrefs.quiet_hours_end,
      digest_frequency: localPrefs.digest_frequency,
      language: localPrefs.language,
    });
  };

  if (isLoading || !localPrefs) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-32 bg-gray-100 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  const Toggle = ({ enabled, onChange, disabled }: { enabled: boolean; onChange: () => void; disabled?: boolean }) => (
    <button
      onClick={onChange}
      disabled={disabled}
      className={`relative w-12 h-6 rounded-full transition-all duration-200 ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      } ${enabled ? 'bg-gradient-to-r from-kenagri-500 to-kenagri-600' : 'bg-gray-200'}`}
    >
      <span
        className={`absolute top-1 h-4 w-4 bg-white rounded-full shadow-soft transition-all duration-200 ${
          enabled ? 'left-7' : 'left-1'
        }`}
      />
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Channels */}
      <Card variant="gradient">
        <CardHeader>
          <CardTitle>Notification Channels</CardTitle>
        </CardHeader>
        <div className="space-y-1">
          {[
            { key: 'in_app', label: 'In-App Notifications', desc: 'Show notifications in the app', alwaysOn: true },
            { key: 'email', label: 'Email', desc: 'Receive notifications via email' },
            { key: 'sms', label: 'SMS', desc: 'Receive notifications via SMS' },
            { key: 'push', label: 'Push Notifications', desc: 'Browser push notifications' },
          ].map(({ key, label, desc, alwaysOn }) => (
            <div key={key} className="flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-colors">
              <div>
                <p className="font-medium text-gray-900">{label}</p>
                <p className="text-sm text-gray-500">{desc}</p>
              </div>
              <Toggle
                enabled={localPrefs.channels?.[key] ?? false}
                disabled={alwaysOn}
                onChange={() =>
                  setLocalPrefs({
                    ...localPrefs,
                    channels: { ...localPrefs.channels, [key]: !localPrefs.channels?.[key] },
                  })
                }
              />
            </div>
          ))}
        </div>
      </Card>

      {/* Categories */}
      <Card variant="gradient">
        <CardHeader>
          <CardTitle>Category Preferences</CardTitle>
        </CardHeader>
        <div className="space-y-1">
          {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => {
            const CatIcon = cfg.icon;
            return (
              <div key={key} className="flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className={`p-1.5 rounded-lg ${cfg.bgColor}`}>
                    <CatIcon className={`h-4 w-4 ${cfg.color}`} />
                  </div>
                  <p className="font-medium text-gray-900">{cfg.label}</p>
                </div>
                <Toggle
                  enabled={localPrefs.categories?.[key] ?? true}
                  onChange={() =>
                    setLocalPrefs({
                      ...localPrefs,
                      categories: { ...localPrefs.categories, [key]: !localPrefs.categories?.[key] },
                    })
                  }
                />
              </div>
            );
          })}
        </div>
      </Card>

      {/* Quiet Hours & Digest */}
      <Card variant="gradient">
        <CardHeader>
          <CardTitle>Schedule</CardTitle>
        </CardHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Quiet Hours Start</label>
            <input
              type="time"
              value={localPrefs.quiet_hours_start || ''}
              onChange={(e) => setLocalPrefs({ ...localPrefs, quiet_hours_start: e.target.value || null })}
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 focus:border-kenagri-500 focus:ring-2 focus:ring-kenagri-500/20 transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Quiet Hours End</label>
            <input
              type="time"
              value={localPrefs.quiet_hours_end || ''}
              onChange={(e) => setLocalPrefs({ ...localPrefs, quiet_hours_end: e.target.value || null })}
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 focus:border-kenagri-500 focus:ring-2 focus:ring-kenagri-500/20 transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Digest Frequency</label>
            <div className="grid grid-cols-2 gap-2">
              {['realtime', 'hourly', 'daily', 'weekly'].map((freq) => (
                <button
                  key={freq}
                  onClick={() => setLocalPrefs({ ...localPrefs, digest_frequency: freq })}
                  className={`px-3 py-2 rounded-lg text-sm font-medium capitalize transition-all ${
                    localPrefs.digest_frequency === freq
                      ? 'bg-kenagri-500 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {freq}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Language</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'en', label: 'English' },
                { value: 'sw', label: 'Swahili' },
              ].map((lang) => (
                <button
                  key={lang.value}
                  onClick={() => setLocalPrefs({ ...localPrefs, language: lang.value })}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    localPrefs.language === lang.value
                      ? 'bg-kenagri-500 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Save */}
      <div className="flex justify-end pt-2">
        <Button
          variant="primary"
          size="lg"
          onClick={handleSave}
          leftIcon={<CheckIcon className="h-5 w-5" />}
        >
          {updateMutation.isPending ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>
    </div>
  );
}

// ============ MAIN PAGE ============

export default function Notifications() {
  const [activeTab, setActiveTab] = useState<'notifications' | 'preferences'>('notifications');
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const observerRef = useRef<HTMLDivElement>(null);

  // Infinite scroll query
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ['notifications', filter, categoryFilter],
    queryFn: async ({ pageParam = 0 }) => {
      const params = new URLSearchParams();
      params.set('limit', '20');
      params.set('offset', String(pageParam));
      if (filter === 'unread') params.set('isRead', 'false');
      if (categoryFilter) params.set('category', categoryFilter);

      const res = await fetch(`${API_URL}/notifications?${params}`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Failed to load notifications');
      return res.json();
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.notifications.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    initialPageParam: 0,
    refetchInterval: 30000,
  });

  // Unread count
  const { data: unreadData } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/notifications/unread-count`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to load count');
      return res.json();
    },
    refetchInterval: 30000,
  });

  // Mutations
  const markReadMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/notifications/${id}/read`, {
        method: 'PATCH',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      const params = categoryFilter ? `?category=${categoryFilter}` : '';
      const res = await fetch(`${API_URL}/notifications/read-all${params}`, {
        method: 'PATCH',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      toast.success(`Marked ${data.marked} notifications as read`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/notifications/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      toast.success('Notification deleted');
    },
  });

  // Infinite scroll observer
  useEffect(() => {
    const el = observerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const allNotifications = data?.pages.flatMap((p) => p.notifications) || [];
  const totalCount = data?.pages[0]?.total || 0;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="text-gray-500 mt-1">
            {unreadData?.count > 0 ? `${unreadData.count} unread` : 'All caught up'}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl mb-6 w-fit">
        {[
          { key: 'notifications' as const, label: 'All Notifications', icon: BellIcon },
          { key: 'preferences' as const, label: 'Preferences', icon: Cog6ToothIcon },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === key
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'preferences' ? (
        <PreferencesTab />
      ) : (
        <>
          {/* Filter Bar */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
              {['all', 'unread'].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f as 'all' | 'unread')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize transition-all ${
                    filter === f ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <FunnelIcon className="h-4 w-4 text-gray-400" />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:border-kenagri-500 focus:ring-1 focus:ring-kenagri-500/20"
              >
                <option value="">All Categories</option>
                {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                  <option key={key} value={key}>{cfg.label}</option>
                ))}
              </select>
            </div>

            <div className="flex-1" />

            {unreadData?.count > 0 && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => markAllReadMutation.mutate()}
                leftIcon={<CheckIcon className="h-4 w-4" />}
              >
                Mark all read
              </Button>
            )}
          </div>

          {/* Notification List */}
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : allNotifications.length === 0 ? (
            <div className="text-center py-16">
              <BellIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-600">No notifications</h3>
              <p className="text-gray-400 mt-1">
                {filter === 'unread'
                  ? 'All notifications have been read.'
                  : 'You have no notifications yet.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {allNotifications.map((n: any) => (
                <NotificationCard
                  key={n.id}
                  notification={n}
                  onMarkRead={(id) => markReadMutation.mutate(id)}
                  onDelete={(id) => deleteMutation.mutate(id)}
                  onNavigate={(link) => navigate(link)}
                />
              ))}

              {/* Infinite scroll sentinel */}
              <div ref={observerRef} className="h-4" />

              {isFetchingNextPage && (
                <div className="flex justify-center py-4">
                  <div className="h-6 w-6 border-2 border-kenagri-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}

              {!hasNextPage && allNotifications.length > 0 && (
                <p className="text-center text-sm text-gray-400 py-4">
                  Showing all {totalCount} notifications
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
